import React, { useState, useEffect } from "react";
import api from "../api/api";
import { Shield, Search, Calendar, FileText, Filter } from "lucide-react";

const MODULE_COLORS: Record<string, string> = {
  AUTH: "bg-blue-100 text-blue-700",
  Users: "bg-purple-100 text-purple-700",
  Tickets: "bg-orange-100 text-orange-700",
  Tasks: "bg-yellow-100 text-yellow-700",
  Projects: "bg-green-100 text-green-700",
  CRM: "bg-pink-100 text-pink-700",
  Quality: "bg-indigo-100 text-indigo-700",
  Approvals: "bg-red-100 text-red-700",
  Communications: "bg-teal-100 text-teal-700",
};

const ACTION_COLORS: Record<string, string> = {
  CREATE:  "bg-green-100 text-green-700",
  UPDATE:  "bg-blue-100 text-blue-700",
  DELETE:  "bg-red-100 text-red-700",
  LOGIN:   "bg-purple-100 text-purple-700",
  LOGOUT:  "bg-gray-100 text-gray-700",
  APPROVE: "bg-teal-100 text-teal-700",
  REJECT:  "bg-orange-100 text-orange-700",
};

const MODULES = ["Users","Tickets","Tasks","Projects","CRM","Quality","Approvals","Communications","AUTH"];

export default function Audit() {
  const [logs, setLogs]               = useState<any[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [from, setFrom]               = useState("");
  const [to, setTo]                   = useState("");
  const [page, setPage]               = useState(1);
  const perPage = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (moduleFilter) params.module = moduleFilter;
      if (from) params.from = from;
      if (to)   params.to   = to;
      const res = await api.get("/audit", { params });
      setLogs(res.data);
      setPage(1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Load all logs on mount (no date restriction)
  useEffect(() => { fetchLogs(); }, []);

  const filtered = logs.filter(l =>
    !search ||
    l.userName?.toLowerCase().includes(search.toLowerCase()) ||
    l.action?.toLowerCase().includes(search.toLowerCase()) ||
    l.module?.toLowerCase().includes(search.toLowerCase()) ||
    (l.details || "").toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const reset = () => {
    setModuleFilter(""); setFrom(""); setTo(""); setSearch(""); setPage(1);
    // re-fetch without filters
    setLoading(true);
    api.get("/audit").then(r => { setLogs(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
          <Shield className="text-white" size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500">Track all system actions and changes</p>
        </div>
        <div className="ml-auto bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
          {filtered.length} records
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search user, action, details..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={moduleFilter}
            onChange={e => setModuleFilter(e.target.value)}
          >
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={fetchLogs}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
            <Filter size={14} /> Apply Filter
          </button>
          <button onClick={reset}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText size={40} className="mb-2" />
            <p>No audit logs found</p>
            <p className="text-xs mt-1">Perform any create / update / delete action to start tracking</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Time","User","Module","Action","Details","Record"].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-medium text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      <div className="flex items-center gap-1">
                        <Calendar size={11} />
                        {new Date(log.createdAt).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-700">{log.userName?.charAt(0)?.toUpperCase() || "?"}</span>
                        </div>
                        <span className="font-medium text-gray-800">{log.userName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${MODULE_COLORS[log.module] || "bg-gray-100 text-gray-700"}`}>
                        {log.module}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-700"}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={log.details || ""}>{(log as any).entityLabel || log.details || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs" title={log.recordId || ""}>{log.recordId ? log.recordId.slice(0,8)+"…" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(page-1)*perPage+1}–{Math.min(page*perPage, filtered.length)} of {filtered.length}
          </p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              className="px-3 py-1 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">← Prev</button>
            {Array.from({length: Math.min(5,totalPages)}, (_,i) => {
              const p = page <= 3 ? i+1 : page-2+i;
              if (p<1||p>totalPages) return null;
              return <button key={p} onClick={()=>setPage(p)}
                className={`px-3 py-1 border rounded-lg text-sm ${p===page?"bg-blue-600 text-white border-blue-600":"border-gray-200 hover:bg-gray-50"}`}>{p}</button>;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              className="px-3 py-1 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
